/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

interface ShowcaseBannerProps {
    bannerUrl: string;
    fallbackText: string;
    size?: 'auto' | 'contain' | 'cover' | 'initial' | 'inherit' | 'unset';
}

/**
 * ShowcaseBanner — displays an image banner.
 * If the image cannot be loaded the provided `fallbackText` is shown centered.
 *
 * @param {string} bannerUrl - Image URL.
 * @param {string} [fallbackText] - Text to show when the image is unavailable.
 * @param size - The CSS background-size property value (default is 'cover').
 */
const ShowcaseBanner: FunctionComponent<ShowcaseBannerProps> = ({
    bannerUrl,
    fallbackText,
    size,
}) => {
    const showcaseBannerRef = useRef<HTMLDivElement>(null);
    const [hasError, setHasError] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        // Reset state to initial values when bannerUrl changes
        setLoading(true);
        setHasError(false);

        // If no URL provided, treat as an error (show fallback)
        if (!bannerUrl) {
            setHasError(true);
            setLoading(false);
            return;
        }

        const img = new Image();
        img.onload = () => {
            if (!isMounted) return;
            setLoading(false);
            setHasError(false);
        };
        img.onerror = () => {
            if (!isMounted) return;
            setLoading(false);
            setHasError(true);
        };
        img.src = bannerUrl;

        // eslint-disable-next-line consistent-return
        return () => {
            isMounted = false;
            img.onload = null;
            img.onerror = null;
        };
    }, [bannerUrl]);

    return (
        <React.Fragment>
            <div
                className="uikit showcase-banner"
                ref={showcaseBannerRef}
                style={{
                    backgroundImage: hasError || loading ? '' : `url(${bannerUrl})`,
                    backgroundSize: size || 'cover',
                }}
            >
                {hasError && fallbackText ? (
                    <span className="uikit showcase-banner-fallback primary-text-style text-size-xlarge">
                        {fallbackText}
                    </span>
                ) : null}
            </div>
        </React.Fragment>
    );
};

export default ShowcaseBanner;
